import { useState, useEffect, useRef } from 'react';

const BANNER = [
  '┌────────────────────────────────────────────────────────────┐',
  '│   feral.cli  ·  Mumbai → world  ·  v2026.05               │',
  '│   reachable: jacob@feraltechlabs.co.in                     │',
  '└────────────────────────────────────────────────────────────┘',
  '',
  'Type `help` for commands, or just describe what you want to build.',
  '',
];

const COMMANDS = {
  help: () => [
    'commands',
    '  help              show this',
    '  about             who we are',
    '  pricing           ballpark figures',
    '  team              the kennel roster',
    '  contact           how to reach us',
    '  clear             clear the screen',
    '',
    'or describe your project in plain english and an agent will respond.',
  ],
  about: () => [
    'feral techlabs',
    '  an eight-person, ai-augmented studio in mumbai.',
    '  we ship in days what most agencies take months on.',
    '  15+ apps shipped · 4 platforms live · sub-ms trading.',
  ],
  pricing: () => [
    'ballpark figures · fixed scope · no surprises',
    '  ui/ux brand system     from  $2,000',
    '  ios app                from  $3,000',
    '  full-stack web         from  $5,000',
    '  ai system              from  $8,000',
    '  security platform      from $10,000',
    '  trading platform       from $15,000',
    '',
    'larger engagements run $50k+ — we quote after a 30-min call.',
  ],
  team: () => [
    '  tanush  · founder / ceo',
    '  jacob   · project manager',
    '  rex     · cto / architect',
    '  koda    · lead full-stack',
    '  iris    · lead designer',
    '  stacy   · security',
    '  vance   · growth',
    '  zara    · qa / devops',
  ],
  contact: () => [
    '  jacob@feraltechlabs.co.in  · project manager · 24/7',
    '  tanushgupta@feraltechlabs.co.in  · ceo · on request',
    '  mumbai, india · working globally',
  ],
  clear: () => '__CLEAR__',
};

const OFFLINE_REPLY =
  "we'd love to talk. email jacob@feraltechlabs.co.in with a one-paragraph brief and we'll reply within 24h.";

const ContactTerminal = () => {
  const [history, setHistory] = useState(() => BANNER.map((l) => ({ kind: 'sys', text: l })));
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const b = bodyRef.current;
    if (b) b.scrollTop = b.scrollHeight;
  }, [history, busy]);

  const submit = async (raw) => {
    const text = raw.trim();
    if (!text) return;

    const cmd = text.toLowerCase().split(' ')[0];
    setHistory((h) => [...h, { kind: 'in', text }]);
    setInput('');

    if (cmd === 'clear') {
      setHistory(BANNER.map((l) => ({ kind: 'sys', text: l })));
      return;
    }
    if (COMMANDS[cmd]) {
      const lines = COMMANDS[cmd]();
      setHistory((h) => [...h, ...lines.map((t) => ({ kind: 'out', text: t }))]);
      return;
    }

    setBusy(true);
    setHistory((h) => [...h, { kind: 'thinking', text: '· thinking …' }]);
    try {
      let reply = OFFLINE_REPLY;
      if (window.claude && typeof window.claude.complete === 'function') {
        const msg = await window.claude.complete({
          messages: [{ role: 'user', content: `User said: ${text}` }],
        });
        reply = (msg || OFFLINE_REPLY).toString().trim();
      } else {
        await new Promise((r) => setTimeout(r, 500));
      }
      setHistory((h) => {
        const next = h.slice(0, -1);
        const lines = reply.split('\n').map((line) => ({ kind: 'out', text: line }));
        return [...next, ...lines];
      });
    } catch (err) {
      setHistory((h) => [
        ...h.slice(0, -1),
        { kind: 'err', text: `error — email jacob@feraltechlabs.co.in` },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !busy) submit(input);
  };

  return (
    <section className="r-section terminal-section" id="contact">
      <div className="r-wrap">
        <div className="r-tag r-reveal">Contact</div>
        <h2 className="r-head r-reveal">
          Talk to an agent.<br />
          <span className="r-gtxt">Or just describe the project.</span>
        </h2>
        <p className="r-sub r-reveal r-d1">
          This is a real terminal. Type <code>help</code> for commands, or write a sentence about
          what you want and hit enter.
        </p>

        <div className="term r-panel r-reveal r-d2" onClick={() => inputRef.current?.focus()}>
          <div className="term-bar">
            <div className="term-dots">
              <span style={{ background: '#ff5f57' }} />
              <span style={{ background: '#febc2e' }} />
              <span style={{ background: '#28c840' }} />
            </div>
            <div className="term-title">jacob@feral · ~/inbox</div>
            <div className="term-status">
              <span className="r-status-pulse" />
              ONLINE
            </div>
          </div>
          <div className="term-body" ref={bodyRef}>
            {history.map((l, i) => (
              <div key={i} className={`tl tl-${l.kind}`}>
                {l.kind === 'in' && <span className="tl-prompt">$ </span>}
                <span>{l.text}</span>
              </div>
            ))}
            <div className="tl tl-input">
              <span className="tl-prompt">$ </span>
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                disabled={busy}
                spellCheck={false}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                aria-label="terminal input"
                placeholder={busy ? '' : 'type something…'}
              />
              {!busy && <span className="tl-caret" />}
            </div>
          </div>
        </div>

        <div className="term-foot r-reveal r-d3">
          <span><strong>jacob@feraltechlabs.co.in</strong> · always on</span>
          <span><strong>tanushgupta@feraltechlabs.co.in</strong> · on request</span>
          <span>mumbai, india</span>
        </div>
      </div>
    </section>
  );
};

export default ContactTerminal;
